import { VerityAssessment } from "@verity/api-client/types";
import { TakeAssessmentSessions } from "../hooks/useFetchAssessmentInfo";
import styles from "./LandingPage.module.css";
import { Button, List, ListItem, Typography } from "@mui/material";
import i18n from "../../../../i18n";
import { baseTransKey } from "../takeAssessmentHelper";
import { useMemo, useState } from "react";
import { CoreExpandIcon, CoreFileCard } from "@verity/components/core";
import { ConditionalWrapper } from "@verity/components";
import { useIsInstructor } from "../../../shared";

function formatSessionTime(endTime?: string | null) {
	if (!endTime) return undefined;

	const dateObj = new Date(endTime);

	// Check for "Invalid Date"
	if (isNaN(dateObj.getTime())) return undefined;

	// Format the date (e.g., "May 8, 2026")
	const date = new Intl.DateTimeFormat(i18n.language, {
		dateStyle: "long",
	}).format(dateObj);

	// Format the time (e.g., "2:47 PM")
	const time = new Intl.DateTimeFormat(i18n.language, {
		timeStyle: "short",
	}).format(dateObj);

	return { date, time };
}

type SessionInfoProps = {
	session: TakeAssessmentSessions[number];
	index: number;
	assessment: VerityAssessment;
};

function SessionInfo({ session, index, assessment }: SessionInfoProps) {
	const [expanded, setExpanded] = useState(false);

	const { score, totalScore, endTime, files } = session;

	const sessionNumber = index + 1;
	const title = i18n.t(`${baseTransKey}.attemptNumber`, { number: sessionNumber }) as string;

	const formattedDateParts = formatSessionTime(endTime);
	const hasFiles = files && files.length > 0;
	const isAssignment = assessment.type === "assignment";

	return (
		<div className={styles.sessionInfoContainer}>
			<ConditionalWrapper
				condition={hasFiles}
				whenTrue={(children) => (
					<Button onClick={() => setExpanded(!expanded)} className={styles.sessionInfoButton} aria-expanded={expanded}>
						{children}
					</Button>
				)}
				whenFalse={(children) => <div className={styles.sessionInfoDiv}>{children}</div>}
			>
				<>
					<div className={styles.sessionInfoTitleContainer}>
						<Typography variant="h3" className={styles.attemptTitle}>
							{title}
						</Typography>
						{formattedDateParts && (
							<Typography className={styles.attemptTime}>
								{i18n.t(`${baseTransKey}.attemptTime`, {
									date: formattedDateParts.date,
									time: formattedDateParts.time,
								})}
							</Typography>
						)}
					</div>
					<div className={styles.sessionInfoScoreContainer}>
						{!isAssignment && assessment.showScoreOnSubmit && (
							<div className={styles.scoreContainer}>
								<Typography className={styles.scoreLabel}>{i18n.t(`${baseTransKey}.yourResult`)}</Typography>
								<Typography className={styles.scoreValue}>
									{score}/{totalScore}
								</Typography>
							</div>
						)}
						{!hasFiles && isAssignment && (
							<div className={styles.scoreContainer}>
								<Typography className={styles.scoreLabel}>{i18n.t(`${baseTransKey}.noFiles`)}</Typography>
							</div>
						)}
						{hasFiles && <CoreExpandIcon expanded={expanded} variant="narrow" />}
					</div>
				</>
			</ConditionalWrapper>
			{expanded && hasFiles && (
				<div className={styles.fileListContainer}>
					<Typography className={styles.fileListTitle}>{i18n.t(`${baseTransKey}.submittedFiles`)}</Typography>
					<List disablePadding className={styles.fileList}>
						{files.map((file, i) => {
							return (
								<ListItem key={i} disablePadding>
									<CoreFileCard file={file} showDownload />
								</ListItem>
							);
						})}
					</List>
				</div>
			)}
		</div>
	);
}

type LandingPageProps = {
	sessions: TakeAssessmentSessions;
	assessmentInfoUI: React.ReactNode;
	verityAssessment: VerityAssessment;
};

export default function LandingPage({ sessions, assessmentInfoUI, verityAssessment }: LandingPageProps) {
	const numAttempts = sessions.length;
	const maxAttempts = verityAssessment.numAttempts;

	const isInstructor = useIsInstructor();

	const title = useMemo(() => {
		if (maxAttempts <= 0 || isInstructor) {
			return i18n.t(`${baseTransKey}.yourAttemptsInfinite`, { count: numAttempts }) as string;
		}
		return i18n.t(`${baseTransKey}.yourAttempts`, { count: numAttempts, max: maxAttempts }) as string;
	}, [numAttempts, maxAttempts, isInstructor]);

	return (
		<div className={styles.landingPageContainer}>
			{assessmentInfoUI}
			<Typography variant="h2" className={styles.title}>
				{title}
			</Typography>
			<List className={styles.sessionsContainer}>
				{sessions.map((session, index) => (
					<SessionInfo key={session.uuid} session={session} index={index} assessment={verityAssessment} />
				))}
			</List>
		</div>
	);
}
